'use client';

// Bandeja del Director de Juzgamiento: expedientes en estado
// PENDIENTE_REPARTO_JUZGAMIENTO. Permite "Asignar" (reparto a un funcionario de
// juzgamiento), "Devolver" (a instrucción, con motivo) y "Ver" el detalle.

import { useEffect, useState } from 'react';
import { Eye, Gavel, Send, Undo2 } from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import HeroVista from '@/components/HeroVista';
import { listarExpedientes, type Expediente } from '@/lib/expedientes';
import {
  repartoJuzgamiento, devolverJuzgamiento, funcionariosJuzgamiento,
  type UsuarioLite,
} from '@/lib/juzgamiento';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

function fmtFecha(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO');
}

function investigadoPrincipal(e: Expediente): string {
  const inv = e.investigados ?? [];
  if (inv.length === 0) return '—';
  const det = inv.find((x) => x.tipo === 'DETERMINADO' && x.nombre) ?? inv[0];
  return det.nombre || (det.tipo === 'POR_ESTABLECER' ? 'Individualizar' : '—');
}

export default function BandejaJuzgamiento({ onAbrir }: { onAbrir?: (id: string) => void }) {
  const [expedientes, setExpedientes] = useState<Expediente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [asignar, setAsignar] = useState<Expediente | null>(null);
  const [devolver, setDevolver] = useState<Expediente | null>(null);

  async function cargar() {
    setCargando(true);
    try {
      const res = await listarExpedientes({ estado: 'PENDIENTE_REPARTO_JUZGAMIENTO', limit: 100 });
      setExpedientes(res.data);
    } catch (e) {
      avisoError('No se pudo cargar la bandeja de juzgamiento', e instanceof Error ? e.message : undefined);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); }, []);

  return (
    <div className="space-y-6">
      <HeroVista
        icono={Gavel}
        titulo="Reparto de Juzgamiento"
        descripcion="Expedientes remitidos a juzgamiento pendientes de asignación. El Director de Juzgamiento los reparte a los funcionarios de juzgamiento o los devuelve a instrucción."
      />

      <Card>
        <CardContent className="pt-6">
          {cargando ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Radicado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Investigado principal</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Prescripción</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expedientes.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">
                      {e.radicado}
                      {e.fecha_hechos_tentativa && (
                        <Badge variant="outline" className="ml-2 border-amber-500/50 text-amber-700 dark:text-amber-400" title="Fecha de los hechos tentativa — falta la real">Fecha tentativa</Badge>
                      )}
                    </TableCell>
                    <TableCell>{fmtFecha(e.fecha_queja)}</TableCell>
                    <TableCell>{investigadoPrincipal(e)}</TableCell>
                    <TableCell><Badge variant="secondary">{e.estado_actual}</Badge></TableCell>
                    <TableCell>{fmtFecha(e.fecha_prescripcion)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => onAbrir?.(e.id)}>
                          <Eye className="size-3.5" /> Ver
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDevolver(e)}>
                          <Undo2 className="size-3.5" /> Devolver
                        </Button>
                        <Button size="sm" onClick={() => setAsignar(e)}>
                          <Send className="size-3.5" /> Asignar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {expedientes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      No hay expedientes pendientes de reparto de juzgamiento.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {asignar && (
        <ModalAsignar
          expediente={asignar}
          onClose={() => setAsignar(null)}
          onListo={(msg) => { setAsignar(null); avisoExito(msg); cargar(); }}
        />
      )}
      {devolver && (
        <ModalDevolver
          expediente={devolver}
          onClose={() => setDevolver(null)}
          onListo={(msg) => { setDevolver(null); avisoExito(msg); cargar(); }}
        />
      )}
    </div>
  );
}

// ---- Modal: asignar (reparto de juzgamiento) ------------------------------
function ModalAsignar({ expediente, onClose, onListo }: {
  expediente: Expediente; onClose: () => void; onListo: (msg: string) => void;
}) {
  const [funcionarios, setFuncionarios] = useState<UsuarioLite[]>([]);
  const [funcionarioId, setFuncionarioId] = useState('');
  const [termino, setTermino] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    funcionariosJuzgamiento()
      .then(setFuncionarios)
      .catch((e) => avisoError('No se pudieron cargar los funcionarios', e instanceof Error ? e.message : undefined));
  }, []);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (!funcionarioId) { avisoError('Seleccione un funcionario.'); return; }
    setGuardando(true);
    try {
      const body: { funcionarioId: string; terminoInterno?: string; observaciones?: string } = { funcionarioId };
      if (termino.trim()) body.terminoInterno = termino.trim();
      if (observaciones.trim()) body.observaciones = observaciones.trim();
      await repartoJuzgamiento(expediente.id, body);
      onListo(`Expediente ${expediente.radicado} asignado a juzgamiento.`);
    } catch (err) {
      avisoError('No se pudo asignar el expediente', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="size-5 text-primary" /> Asignar a juzgamiento</DialogTitle>
          <DialogDescription>Radicado: <strong>{expediente.radicado}</strong></DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div className="space-y-2">
            <Label>Funcionario de juzgamiento</Label>
            <Select value={funcionarioId} onValueChange={setFuncionarioId}>
              <SelectTrigger className="w-full"><SelectValue placeholder="— Seleccione —" /></SelectTrigger>
              <SelectContent>
                {funcionarios.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.nombre} {f.apellido} · {f.rol}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="termino">Término interno (opcional)</Label>
            <Input id="termino" value={termino} onChange={(e) => setTermino(e.target.value)} placeholder="Ej: 30 días" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="obs">Observaciones (opcional)</Label>
            <Textarea id="obs" rows={3} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Asignando…' : 'Asignar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Modal: devolver a instrucción ----------------------------------------
function ModalDevolver({ expediente, onClose, onListo }: {
  expediente: Expediente; onClose: () => void; onListo: (msg: string) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (!motivo.trim()) { avisoError('Indique el motivo de la devolución.'); return; }
    setGuardando(true);
    try {
      await devolverJuzgamiento(expediente.id, { motivo: motivo.trim() });
      onListo(`Expediente ${expediente.radicado} devuelto a instrucción.`);
    } catch (err) {
      avisoError('No se pudo devolver el expediente', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Undo2 className="size-5 text-primary" /> Devolver a instrucción</DialogTitle>
          <DialogDescription>Radicado: <strong>{expediente.radicado}</strong></DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="motivo">Motivo de la devolución</Label>
            <Textarea id="motivo" rows={4} value={motivo} onChange={(e) => setMotivo(e.target.value)} required />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Devolviendo…' : 'Devolver'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
